import React from "react";
import ScoreCard from "../../incbotFeedback/ScoreCard";
import { FileText, MessageSquare, Star } from "lucide-react";
import { useSelector } from "react-redux";
import { getFeedbackForm } from "../../../../redux/reducer/incbotFeedbackReducer";

/** Returns color and label based on CEFR level string */
const getCefrCardStatus = (level: string): { title: string; color: "blue" | "green" | "teal" } => {
  const map: Record<string, number> = {
    A0: 0,
    A1: 15,
    A2: 30,
    "B1-": 40,
    B1: 50,
    "B1+": 55,
    "B2-": 60,
    B2: 70,
    "B2+": 75,
    "C1-": 80,
    C1: 85,
    "C1+": 90,
    "C2-": 95,
    C2: 100,
  };
  const score = map[level?.replace(/\s/g, "")] ?? -1;
  if (score < 0) return { title: "—", color: "blue" };
  if (score <= 30) return { title: "Beginner", color: "blue" };
  if (score <= 55) return { title: "Intermediate", color: "teal" };
  if (score <= 75) return { title: "Upper-Intermediate", color: "green" };
  return { title: "Advanced", color: "green" };
};

/** Returns color and label based on percentage score */
const getCefrPctStatus = (pct: number): { title: string; color: "blue" | "green" | "teal" } => {
  if (pct >= 80) return { title: "Excellent", color: "green" };
  if (pct >= 60) return { title: "Good", color: "teal" };
  if (pct >= 40) return { title: "Average", color: "blue" };
  return { title: "Below Average", color: "blue" };
};

interface CefrScoreCardProps {
  value: string | number;
  label: string;
  assessment: string;
  color?: "blue" | "green" | "teal";
}

interface CefrScoreCardWithStarProps extends CefrScoreCardProps {
  showStar?: boolean;
}

const CefrScoreCard: React.FC<CefrScoreCardWithStarProps> = ({
  value,
  label,
  assessment,
  color = "blue",
  showStar = false,
}) => {
  const isGreen = color === "green";
  const isTeal = color === "teal";
  const textColor = isGreen ? "text-green-600" : isTeal ? "text-[#12B6BC]" : "text-blue-600";
  return (
    <div
      className={`p-2 md:p-3 rounded-lg border text-center flex flex-col items-center ${
        isGreen
          ? "bg-green-50 border-green-200"
          : isTeal
            ? "bg-[#f3fafd] border-[#12B6BC]"
            : "bg-blue-50 border-blue-200"
      }`}
    >
      {showStar && <Star className={`w-5 h-5 mb-2 ${textColor}`} strokeWidth={1.5} />}
      <div className={`text-2xl md:text-2xl font-bold mb-1 ${textColor}`}>{value}</div>
      <h3 className={`text-sm md:text-md font-semibold mb-1 ${textColor}`}>{label}</h3>
      <p className={`text-xs md:text-sm ${textColor}`}>{assessment}</p>
    </div>
  );
};

const OverviewView: React.FC = () => {
  const feedbackForm = useSelector(getFeedbackForm);

  const cefrLevelValue = feedbackForm?.overallFeedback?.[0]?.value ?? 0;
  const cefrLevelLabel = (feedbackForm as any)?.cefrLevelLabel ?? "—";
  const overallScoreValue: number | null = (feedbackForm as any)?.overallScoreValue ?? null;
  const overallScoreLabel: string | null = (feedbackForm as any)?.overallScoreLabel ?? null;
  const totalLabel = cefrLevelLabel || overallScoreLabel;
  const totalNumeric =
    cefrLevelValue * 100 || overallScoreValue || Math.round(cefrLevelValue * 100);
  const resumeRating = feedbackForm?.overallFeedback?.[1]?.value ?? 0;
  const percentileValue = feedbackForm?.overallFeedback?.[2]?.value ?? 0;
  const suspiciousValue = feedbackForm?.overallFeedback?.[3]?.value ?? 0;
  const totalParticipants = (feedbackForm as any)?.totalParticipants ?? 0;

  const percentage = (feedbackForm?.score as { percentage?: number })?.percentage ?? 0;

  const getSuspiciousLevel = (value: number) => {
    if (value > 7)
      return {
        level: "HIGH",
        type: "alert" as const,
        color: "red" as const,
        progress: 100,
      };
    if (value > 4)
      return {
        level: "MEDIUM",
        type: "percentile" as const,
        color: "yellow" as const,
        progress: 50,
      };
    return {
      level: "LOW",
      type: "score" as const,
      color: "blue" as const,
      progress: 0,
    };
  };

  const { level, type, color, progress: suspiciousProgress } = getSuspiciousLevel(suspiciousValue);

  const cefrScores = feedbackForm?.cefrScores;
  const activeSections: string[] = (feedbackForm as any)?.cefrActiveSections ?? [];

  const hasReading = activeSections.includes("Reading");
  const hasListening = activeSections.includes("Listening");
  const hasWriting = activeSections.includes("Writing");
  const hasSpeaking = activeSections.includes("Speaking");

  const reading = cefrScores?.reading;
  const listening = cefrScores?.listening;
  const writing = cefrScores?.writing;
  const speaking = cefrScores?.speaking;

  const cefrToScore = (lvl: string) => {
    const map: Record<string, number> = {
      A0: 0,
      A1: 15,
      A2: 30,
      "B1-": 40,
      B1: 50,
      "B1+": 55,
      "B2-": 60,
      B2: 70,
      "B2+": 75,
      "C1-": 80,
      C1: 85,
      "C1+": 90,
      "C2-": 95,
      C2: 100,
    };
    return map[lvl?.replace(/\s/g, "")] ?? 0;
  };

  const numericToCefrLabel = (numeric: number): string => {
    const levels: [string, number][] = [
      ["A0", 0],
      ["A1", 15],
      ["A2", 30],
      ["B1-", 40],
      ["B1", 50],
      ["B1+", 55],
      ["B2-", 60],
      ["B2", 70],
      ["B2+", 75],
      ["C1-", 80],
      ["C1", 85],
      ["C1+", 90],
      ["C2-", 95],
      ["C2", 100],
    ];
    let closest = "A0";
    let minDiff = Infinity;
    for (const [label, score] of levels) {
      const diff = Math.abs(score - numeric);
      if (diff < minDiff) {
        minDiff = diff;
        closest = label;
      }
    }
    return closest;
  };

  const sectionScores = [
    ...(hasListening && listening
      ? [
          {
            name: "Listening",
            label: listening.finalCefrLevel || numericToCefrLabel(listening.percentageScore ?? 0),
            numeric: cefrToScore(listening.finalCefrLevel) || (listening.percentageScore ?? 0),
          },
        ]
      : []),
    ...(hasReading && reading
      ? [
          {
            name: "Reading",
            label: reading.finalCefrLevel || numericToCefrLabel(reading.percentageScore ?? 0),
            numeric: cefrToScore(reading.finalCefrLevel) || (reading.percentageScore ?? 0),
          },
        ]
      : []),
    ...(hasWriting && writing
      ? [
          {
            name: "Writing",
            label: writing.overallCefr || "—",
            numeric: cefrToScore(writing.overallCefr),
          },
        ]
      : []),
    ...(hasSpeaking && speaking
      ? [
          {
            name: "Speaking",
            label: speaking.overallCefr || "—",
            numeric: cefrToScore(speaking.overallCefr),
          },
        ]
      : []),
    { name: "Total", label: totalLabel, numeric: totalNumeric },
  ];

  return (
    <div className="p-3 md:p-6">
      <div className="md:hidden print:flex w-100 p-1 print:p-2 rounded-md border-b-4 border-teal-500 bg-teal-50 text-teal-500 text-md print:text-xl flex justify-center font-semibold mb-3 md:mb-6">
        Overview
      </div>

      <div className="grid grid-cols-1 md:grid-cols-4 gap-3 md:gap-3 mb-3 md:mb-4">
        <ScoreCard
          title="CEFR Level"
          value={cefrLevelLabel}
          subtitle="Average language proficiency"
          type="score"
          progress={cefrLevelValue * 100}
          color="teal"
        />
        <ScoreCard
          title="Resume Score"
          value={String(resumeRating * 10) + "%"}
          subtitle="Total percentage"
          type="resume"
          progress={resumeRating * 10}
          color="blue"
        />
        <ScoreCard
          title="Percentile"
          value={percentileValue}
          subtitle={`Out of ${totalParticipants} test takers`}
          type="percentile"
          progress={percentileValue}
          color="green"
        />
        <ScoreCard
          title="Suspicious Activity"
          value={level}
          subtitle="Suspicious activity detected"
          type={type}
          progress={suspiciousProgress}
          color={color}
        />
      </div>

      <div className="grid grid-cols-1 gap-3 md:gap-3">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-3 md:gap-6">
          {feedbackForm?.resumeSummary && (
            <div className="bg-white p-4 md:p-6 rounded-lg border border-gray-200">
              <div className="flex items-center space-x-2 mb-3 md:mb-4">
                <FileText className="w-4 h-4 md:w-5 md:h-5 text-gray-600" />
                <h2 className="text-md md:text-lg font-semibold text-gray-900">Resume Summary</h2>
              </div>
              <div className="space-y-1 md:space-y-3">
                {feedbackForm.resumeSummary
                  .split("\n")
                  .filter((point: string) => point.trim())
                  .map((point: string, index: number) => (
                    <div key={index} className="flex items-start space-x-2">
                      <div className="h-1 w-1 md:w-2 md:h-2 bg-blue-500 rounded-full mt-2 flex-shrink-0"></div>
                      <p className="text-xs md:text-sm text-gray-700">
                        {point.replace(/^-\s*/, "")}
                      </p>
                    </div>
                  ))}
              </div>
            </div>
          )}

          {/* Section-wise Scores - always visible */}
          <div className="bg-white p-4 md:p-6 rounded-lg border border-gray-200">
            <h2 className="text-md md:text-lg font-semibold text-gray-900 mb-3 md:mb-6">
              Section-wise Scores
            </h2>
            <div className="space-y-4">
              {sectionScores.map((section, index) => (
                <div key={index} className="flex items-center flex-col w-full">
                  <div className="flex items-center justify-between w-full mb-2">
                    <span className="text-xs md:text-sm font-medium text-gray-700">
                      {section.name}
                    </span>
                    <span className="text-xs md:text-sm font-semibold text-gray-700">
                      {section.label}
                    </span>
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-1 md:h-2">
                    <div
                      className="h-1 md:h-2 bg-blue-500 rounded-full"
                      style={{ width: `${Math.min(100, section.numeric)}%` }}
                    />
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Reading, Listening, Writing, Speaking Scores - odd one takes full width */}
        {(() => {
          const panels = [
            ...(hasListening && listening
              ? [
                  {
                    key: "listening",
                    title: "Listening Score",
                    content: (
                      <div className="grid grid-cols-3 gap-3">
                        <CefrScoreCard
                          value={listening.rawScore}
                          label="Raw Score"
                          assessment={getCefrPctStatus(listening.percentageScore).title}
                          color={getCefrPctStatus(listening.percentageScore).color}
                          showStar
                        />
                        <CefrScoreCard
                          value={`${listening.percentageScore}%`}
                          label="Percentage Score"
                          assessment={getCefrPctStatus(listening.percentageScore).title}
                          color={getCefrPctStatus(listening.percentageScore).color}
                          showStar
                        />
                        <CefrScoreCard
                          value={listening.finalCefrLevel}
                          label="Final CEFR Level"
                          assessment={getCefrCardStatus(listening.finalCefrLevel).title}
                          color={getCefrCardStatus(listening.finalCefrLevel).color}
                          showStar
                        />
                      </div>
                    ),
                  },
                ]
              : []),
            ...(hasReading && reading
              ? [
                  {
                    key: "reading",
                    title: "Reading Score",
                    content: (
                      <div className="grid grid-cols-3 gap-3">
                        <CefrScoreCard
                          value={reading.rawScore}
                          label="Raw Score"
                          assessment={getCefrPctStatus(reading.percentageScore).title}
                          color={getCefrPctStatus(reading.percentageScore).color}
                          showStar
                        />
                        <CefrScoreCard
                          value={`${reading.percentageScore}%`}
                          label="Percentage Score"
                          assessment={getCefrPctStatus(reading.percentageScore).title}
                          color={getCefrPctStatus(reading.percentageScore).color}
                          showStar
                        />
                        <CefrScoreCard
                          value={reading.finalCefrLevel}
                          label="Final CEFR Level"
                          assessment={getCefrCardStatus(reading.finalCefrLevel).title}
                          color={getCefrCardStatus(reading.finalCefrLevel).color}
                          showStar
                        />
                      </div>
                    ),
                  },
                ]
              : []),
            ...(hasWriting && writing
              ? [
                  {
                    key: "writing",
                    title: "Writing Score",
                    content: (
                      <div className="grid grid-cols-2 gap-3">
                        <CefrScoreCard
                          value={writing.vocabulary}
                          label="Vocabulary"
                          assessment={getCefrCardStatus(writing.vocabulary).title}
                          color={getCefrCardStatus(writing.vocabulary).color}
                          showStar
                        />
                        <CefrScoreCard
                          value={writing.coherence}
                          label="Coherence"
                          assessment={getCefrCardStatus(writing.coherence).title}
                          color={getCefrCardStatus(writing.coherence).color}
                          showStar
                        />
                        <CefrScoreCard
                          value={writing.grammar}
                          label="Grammar"
                          assessment={getCefrCardStatus(writing.grammar).title}
                          color={getCefrCardStatus(writing.grammar).color}
                          showStar
                        />
                        <CefrScoreCard
                          value={writing.overallCefr}
                          label="Overall CEFR"
                          assessment={getCefrCardStatus(writing.overallCefr).title}
                          color={getCefrCardStatus(writing.overallCefr).color}
                          showStar
                        />
                      </div>
                    ),
                  },
                ]
              : []),
            ...(hasSpeaking && speaking
              ? [
                  {
                    key: "speaking",
                    title: "Speaking Score",
                    content: (
                      <div className="grid grid-cols-2 gap-3">
                        <CefrScoreCard
                          value={speaking.pronunciation}
                          label="Pronunciation"
                          assessment={getCefrCardStatus(speaking.pronunciation).title}
                          color={getCefrCardStatus(speaking.pronunciation).color}
                          showStar
                        />
                        <CefrScoreCard
                          value={speaking.fluency}
                          label="Fluency"
                          assessment={getCefrCardStatus(speaking.fluency).title}
                          color={getCefrCardStatus(speaking.fluency).color}
                          showStar
                        />
                        <CefrScoreCard
                          value={speaking.grammar}
                          label="Grammar"
                          assessment={getCefrCardStatus(speaking.grammar).title}
                          color={getCefrCardStatus(speaking.grammar).color}
                          showStar
                        />
                        <CefrScoreCard
                          value={speaking.overallCefr}
                          label="Overall CEFR"
                          assessment={getCefrCardStatus(speaking.overallCefr).title}
                          color={getCefrCardStatus(speaking.overallCefr).color}
                          showStar
                        />
                      </div>
                    ),
                  },
                ]
              : []),
          ];

          const rows: React.ReactNode[] = [];
          for (let i = 0; i < panels.length; i += 2) {
            const first = panels[i];
            const second = panels[i + 1];
            rows.push(
              <div
                key={first.key}
                className={`grid grid-cols-1 ${second ? "lg:grid-cols-2" : ""} gap-3 md:gap-6`}
              >
                <div className="bg-white p-4 md:p-6 rounded-lg border border-gray-200">
                  <div className="flex items-center space-x-2 mb-4">
                    <div className="p-1.5 rounded bg-teal-50">
                      <MessageSquare className="w-4 h-4 md:w-5 md:h-5 text-teal-600" />
                    </div>
                    <h2 className="text-md md:text-lg font-semibold text-gray-900">
                      {first.title}
                    </h2>
                  </div>
                  {first.content}
                </div>
                {second && (
                  <div className="bg-white p-4 md:p-6 rounded-lg border border-gray-200">
                    <div className="flex items-center space-x-2 mb-4">
                      <div className="p-1.5 rounded bg-teal-50">
                        <MessageSquare className="w-4 h-4 md:w-5 md:h-5 text-teal-600" />
                      </div>
                      <h2 className="text-md md:text-lg font-semibold text-gray-900">
                        {second.title}
                      </h2>
                    </div>
                    {second.content}
                  </div>
                )}
              </div>
            );
          }
          return rows;
        })()}
      </div>
    </div>
  );
};

export default OverviewView;
